import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

import { 
  RefreshCw, 
  Crown, 
  Trophy, 
  Medal, 
  Award, 
  ExternalLink,
  CheckCircle,
} from 'lucide-react';
import { formatDate, formatRating, getRatingColor } from '@/lib/utils';
import { ChessUser } from '@/types';

export const Profile: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hasSkippedSetup, setHasSkippedSetup] = useState(false);

  const { data: profileData, isLoading, error } = useQuery(
    'userProfile',
    () => apiClient.getUserProfile(),
    {
      retry: false,
      onError: (error) => {
        console.log('Profile query error:', error);
        // Profile doesn't exist, user needs to initialize
      }
    }
  );

  // Debug logging
  console.log('Profile component state:', { isLoading, error, profileData, hasSkippedSetup });

  // Redirect to ChessSetup if user doesn't have a profile and hasn't skipped
  useEffect(() => {
    if (!isLoading && (error || !profileData?.data) && !hasSkippedSetup) {
      navigate('/chess-setup');
    }
  }, [isLoading, error, profileData, navigate, hasSkippedSetup]);

  // Check if user came from ChessSetup with skip
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('skipped') === 'true') {
      setHasSkippedSetup(true);
    }
  }, []);

  const syncMutation = useMutation(
    () => apiClient.syncUser(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userProfile');
        queryClient.invalidateQueries('leaderboard');
      }
    }
  );

  const handleSync = () => {
    syncMutation.mutate();
  };

  const getLastSyncText = (lastSync: string) => {
    const now = new Date();
    const lastSyncDate = new Date(lastSync);
    const diffInHours = Math.floor((now.getTime() - lastSyncDate.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        <div className="animate-pulse space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center space-x-4">
              <div className="bg-gray-200 h-16 w-16 rounded-full"></div>
              <div className="space-y-2">
                <div className="bg-gray-200 h-4 w-32 rounded"></div>
                <div className="bg-gray-200 h-3 w-24 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User hasn't initialized their profile yet
  if (error || !profileData?.data) {
    // If user has skipped setup, show a different view
    if (hasSkippedSetup) {
      return (
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
            <p className="text-gray-600 mt-2">
              Welcome to ChessHub! You can set up your Chess.com profile later.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Setup Your Chess.com Profile</CardTitle>
              <p className="text-sm text-gray-600">
                Connect your Chess.com account to join the leaderboard and compete with colleagues.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  You chose to skip the Chess.com setup. You can always set up your profile later.
                </p>
              </div>
              <Button
                onClick={() => navigate('/chess-setup')}
                className="w-full"
              >
                <Crown className="h-4 w-4 mr-2" />
                Connect Chess.com Profile Now
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Your Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="text-orange-600 font-medium">Chess.com profile not connected</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Access Level:</span>
                  <span className="text-green-600 font-medium">Full access to community features</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Show loading while redirecting to setup
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to setup...</p>
        </div>
      </div>
    );
  }

  const profile: ChessUser = profileData.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-600 mt-1">Manage your ChessHub profile and ratings</p>
        </div>
        <Button
          onClick={handleSync}
          loading={syncMutation.isLoading}
          disabled={syncMutation.isLoading}
          variant="outline"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Sync Data
        </Button>
      </div>

      {/* Profile Info */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            {profile.avatar ? (
              <img
                src={profile.avatar}
                alt={profile.name}
                className="h-16 w-16 rounded-full"
              />
            ) : (
              <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-xl font-medium text-gray-600">
                  {profile.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{profile.name}</h3>
              <p className="text-gray-600">{profile.email}</p>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-sm text-gray-500">Chess.com:</span>
                <a
                  href={profile.profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                >
                  <span>@{profile.chessUsername}</span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ratings */}
      <Card>
        <CardHeader>
          <CardTitle>Your Ratings</CardTitle>
          <p className="text-sm text-gray-600">
            Last synced: {getLastSyncText(profile.stats.lastSyncedAt)}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg bg-blue-50">
              <div className="flex items-center justify-center mb-2">
                <Crown className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-gray-600">Rapid</p>
              <p className={`text-xl font-bold ${getRatingColor(profile.stats.rapid)}`}>
                {formatRating(profile.stats.rapid)}
              </p>
            </div>
            <div className="text-center p-4 rounded-lg bg-green-50">
              <div className="flex items-center justify-center mb-2">
                <Trophy className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-sm font-medium text-gray-600">Blitz</p>
              <p className={`text-xl font-bold ${getRatingColor(profile.stats.blitz)}`}>
                {formatRating(profile.stats.blitz)}
              </p>
            </div>
            <div className="text-center p-4 rounded-lg bg-orange-50">
              <div className="flex items-center justify-center mb-2">
                <Medal className="h-6 w-6 text-orange-600" />
              </div>
              <p className="text-sm font-medium text-gray-600">Bullet</p>
              <p className={`text-xl font-bold ${getRatingColor(profile.stats.bullet)}`}>
                {formatRating(profile.stats.bullet)}
              </p>
            </div>
            <div className="text-center p-4 rounded-lg bg-purple-50">
              <div className="flex items-center justify-center mb-2">
                <Award className="h-6 w-6 text-purple-600" />
              </div>
              <p className="text-sm font-medium text-gray-600">Puzzles</p>
              <p className={`text-xl font-bold ${getRatingColor(profile.stats.puzzles)}`}>
                {formatRating(profile.stats.puzzles)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Joined</span>
            <span className="font-medium">{formatDate(profile.joinedAt)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Domain</span>
            <span className="font-medium">{profile.domain}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Status</span>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-green-600 font-medium">Active</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connect Chess.com Profile Alert - Show if user doesn't have Chess.com profile */}
      {!profile.chessUsername && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-800">Connect Your Chess.com Profile</CardTitle>
            <p className="text-sm text-orange-700">
              You haven't connected your Chess.com profile yet. Connect it to join the leaderboard and compete with your colleagues!
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/chess-setup')}
                className="flex-1"
              >
                <Crown className="h-4 w-4 mr-2" />
                Connect Chess.com Profile
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('https://www.chess.com/register', '_blank')}
                className="flex-1"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Create Chess.com Account
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 